import { useState, type FormEvent } from 'react'
import { useAuth } from '../auth/AuthContext'
import { Button } from '../components/Button'
import { Field } from '../components/Field'
import { Logo } from '../components/Logo'
import { getDb } from '../lib/db'

/**
 * Onboarding state for signed-in users without any organization membership.
 * Creating an organization triggers an owner membership (DB trigger) and
 * re-enters the main app.
 */
export function NoOrganizationPage() {
  const { user, signOut, refreshMemberships } = useAuth()
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const trimmed = name.trim()
    if (trimmed.length === 0) {
      setError('Please enter an organization name.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      await getDb().createOrganization({ name: trimmed })
      await refreshMemberships()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the organization')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-10 bg-ground px-6 py-16">
      <header className="flex w-full max-w-md items-center justify-between">
        <Logo />
        <Button variant="ghost" size="sm" onClick={() => void signOut()}>
          Sign out
        </Button>
      </header>

      <main className="fade-up w-full max-w-md">
        <div className="flex flex-col gap-8 rounded-2xl border border-line bg-ground-2 p-8">
          <div className="flex flex-col gap-3">
            <h1 className="font-display text-display-m text-ink">No organization yet</h1>
            <p className="text-body text-ink-muted">
              Your account ({user?.email}) isn't part of an organization workspace. Create one to get
              started, or ask an owner to invite you and then reload.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <Field
              label="Organization name"
              name="organizationName"
              placeholder="Acme Studio"
              autoComplete="organization"
              value={name}
              onChange={(event) => setName(event.target.value)}
              error={error}
              disabled={submitting}
            />
            <Button type="submit" size="lg" fullWidth loading={submitting}>
              Create organization
            </Button>
          </form>
        </div>
      </main>

      <footer className="text-small text-ink-faint">nMach Portal · Porcelain Studio</footer>
    </div>
  )
}
